import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Container,
  Avatar,
  LinearProgress,
  Alert,
  StepConnector,
  alpha,
  styled
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LessonCreator from '../componets/LessonCreator/LessonCreator';
import TestCreator from '../componets/TestCreator/TestCreator';
import { getCourse, updateCourse, getCourseHistory, deleteCourse, updateGMPCourse } from '../services/api';
import { processImagesInContent, compressImage, getImageUrl, handleImageError, validateImage } from '../utils/imageUtils';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436',
  light: '#dfe6e9'
};

// Colores por categoría
const CATEGORY_COLORS = {
  'Mantenimiento': { color: COLORS.warning, icon: '🔧' },
  'Producción': { color: COLORS.success, icon: '🏭' },
  'Proceso': { color: COLORS.purple, icon: '⚙️' },
  'Calidad': { color: COLORS.primary, icon: '✅' },
  'Seguridad': { color: COLORS.error, icon: '🛡️' },
  'General': { color: COLORS.orange, icon: '📖' }
};

// Helper para obtener color de categoría de forma segura
const getCategoryConfig = (category) => {
  const defaultConfig = { color: COLORS.info, icon: '📖' };
  if (!category || typeof category !== 'string') return defaultConfig;
  const config = CATEGORY_COLORS[category];
  if (!config || !config.color) return defaultConfig;
  return config;
};

// Helper seguro para alpha - evita errores si color es undefined
const safeAlpha = (color, opacity) => {
  const defaultRgba = `rgba(116, 185, 255, ${opacity})`; // fallback a COLORS.info
  if (!color || typeof color !== 'string') return defaultRgba;
  // Verificar que el color tiene formato válido
  if (!color.startsWith('#') && !color.startsWith('rgb')) return defaultRgba;
  try {
    return alpha(color, opacity); // Usar alpha de MUI
  } catch (e) {
    console.warn('safeAlpha error with color:', color, e);
    return defaultRgba;
  }
};

// Reemplazar alpha por safeAlpha en todo el componente para mayor seguridad
const useAlpha = safeAlpha;

// Styled Step Connector
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: '#e0e0e0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
  '&.Mui-active .MuiStepConnector-line': {
    background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
    borderColor: 'transparent',
    height: 3,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
    borderColor: 'transparent',
    height: 3,
  },
}));

const EditCourse = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [changeReason, setChangeReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const categories = ['Mantenimiento', 'Producción', 'Proceso', 'Calidad', 'General'];
  
  const steps = [
    { label: 'Información', icon: <InfoIcon /> },
    { label: 'Lecciones', icon: <MenuBookIcon /> },
    { label: 'Test Final', icon: <QuizIcon /> },
    { label: 'Configuración', icon: <SettingsIcon /> },
    { label: 'Revisión', icon: <CheckCircleIcon /> }
  ];

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const data = await getCourse(id);
        
        const processedData = {
          ...data,
          test: {
            questions: Array.isArray(data.test?.questions) ? data.test.questions : [],
            config: {
              questions_per_test: data.questions_per_test || data.test?.config?.questions_per_test || 10,
              rotateQuestions: data.test?.config?.rotateQuestions ?? true,
              requireAllDifficulties: data.test?.config?.requireAllDifficulties ?? false
            }
          },
          coursePdfs: Array.isArray(data.course_pdf_path) ? 
            data.course_pdf_path : 
            (typeof data.course_pdf_path === 'string' ? 
              JSON.parse(data.course_pdf_path || '[]') : []),
          courseImages: Array.isArray(data.course_image_path) ? 
            data.course_image_path : 
            (typeof data.course_image_path === 'string' ? 
              JSON.parse(data.course_image_path || '[]') : []),
          courseVideos: Array.isArray(data.course_video_path) ? 
            data.course_video_path : 
            (typeof data.course_video_path === 'string' ? 
              JSON.parse(data.course_video_path || '[]') : []),
          lessons: Array.isArray(data.lessons) ? data.lessons : [],
          cover_image_path: data.cover_image_path || null
        };
  
        setCourseData(processedData);
      } catch (err) {
        console.error('Error loading course:', err);
        setError('Error al cargar el curso');
      } finally {
        setLoading(false);
      }
    };
  
    fetchCourse();
  }, [id]);

  const fetchHistory = async () => {
    try {
      const historyData = await getCourseHistory(id);
      setHistory(historyData);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleOpenHistory = () => {
    fetchHistory();
    setHistoryOpen(true);
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCourseData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleLessonsChange = async (newLessons) => {
    const processedLessons = await Promise.all(newLessons.map(async (lesson) => {
      const processedContent = await processImagesInContent(lesson.content);
      return { ...lesson, content: processedContent };
    }));
    setCourseData(prevData => ({ ...prevData, lessons: processedLessons }));
  };

  const handleTestChange = (newTest) => {
    if (!newTest) return;
    
    const updatedTest = {
      questions: newTest.questions || [],
      config: {
        questions_per_test: newTest.config?.questions_per_test || 10,
        rotateQuestions: newTest.config?.rotateQuestions ?? true,
        requireAllDifficulties: newTest.config?.requireAllDifficulties ?? false
      }
    };
    
    setCourseData(prevData => ({ ...prevData, test: updatedTest }));
  };

  const handleMinPassingScoreChange = (event, newValue) => {
    setCourseData(prevData => ({ ...prevData, min_passing_score: newValue }));
  };

  const handleCoverImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const compressedImage = await compressImage(reader.result);
          setCourseData(prevData => ({
            ...prevData,
            coverImage: compressedImage,
            cover_image_path: undefined
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error processing cover image:', error);
        alert('Error al procesar la imagen de portada');
      }
    }
  };

  const handleFileChange = (event, fileType) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setCourseData(prevData => {
        const existingFiles = prevData[fileType] || [];
        return {
          ...prevData,
          [fileType]: [...existingFiles, ...files]
        };
      });
    }
  };

  const handleRemoveFile = (fileType, index) => {
    setCourseData(prevData => ({
      ...prevData,
      [fileType]: prevData[fileType].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!changeReason.trim()) {
      alert('Por favor, ingrese una razón para el cambio (requerido para GMP)');
      return;
    }
  
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      
      formData.append('title', courseData.title.trim());
      formData.append('description', courseData.description.trim());
      formData.append('min_passing_score', courseData.min_passing_score);
      formData.append('category', courseData.category);
      formData.append('changeReason', changeReason);
  
      if (courseData.coverImage) {
        const blob = await fetch(courseData.coverImage).then(r => r.blob());
        formData.append('coverImage', blob, 'cover.jpg');
      }
  
      if (courseData.cover_image_path) {
        formData.append('cover_image_path', courseData.cover_image_path.replace(/\\/g, '/'));
      }
  
      // PDFs
      const existingPdfs = courseData.coursePdfs
        .filter(f => typeof f === 'string')
        .map(path => path.replace(/\\/g, '/'));
      const newPdfs = courseData.coursePdfs.filter(f => f instanceof File);
      
      formData.append('coursePdfs_existing', JSON.stringify(existingPdfs));
      newPdfs.forEach(file => formData.append('coursePdf', file));
  
      // Imágenes
      const existingImages = courseData.courseImages
        .filter(f => typeof f === 'string')
        .map(path => path.replace(/\\/g, '/'));
      const newImages = courseData.courseImages.filter(f => f instanceof File);
      
      formData.append('courseImages_existing', JSON.stringify(existingImages));
      newImages.forEach(file => formData.append('courseImage', file));
  
      // Videos
      const existingVideos = courseData.courseVideos
        .filter(f => typeof f === 'string')
        .map(path => path.replace(/\\/g, '/'));
      const newVideos = courseData.courseVideos.filter(f => f instanceof File);
      
      formData.append('courseVideos_existing', JSON.stringify(existingVideos));
      newVideos.forEach(file => formData.append('courseVideo', file));
  
      formData.append('lessons', JSON.stringify(courseData.lessons));
  
      const testData = {
        questions: courseData.test.questions.map(question => ({
          question: question.question,
          options: question.options,
          correctAnswer: question.correctAnswer,
          difficulty: question.difficulty
        })),
        config: {
          questions_per_test: courseData.test.config.questions_per_test,
          rotateQuestions: courseData.test.config.rotateQuestions,
          requireAllDifficulties: courseData.test.config.requireAllDifficulties
        }
      };
      formData.append('test', JSON.stringify(testData));
  
      const response = await updateCourse(id, formData);
      const gmpResponse = await updateGMPCourse(id, formData);
  
      if (response.data.success && gmpResponse.data.success) {
        alert('Curso actualizado exitosamente');
        setTimeout(() => navigate('/courses'), 1000);
      }
    } catch (error) {
      console.error('Error updating course:', error);
      alert('Error al actualizar el curso. Por favor, inténtelo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (window.confirm('¿Está seguro de que desea desactivar este curso?')) {
      try {
        await deleteCourse(id);
        navigate('/courses');
      } catch (err) {
        console.error('Error al desactivar el curso:', err);
        setError('Error al desactivar el curso.');
      }
    }
  };

  const renderFileList = (fileType, label, icon, color) => (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {React.cloneElement(icon, { sx: { color } })}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark }}>
          {label}
        </Typography>
        <Chip 
          label={courseData[fileType]?.length || 0} 
          size="small" 
          sx={{ bgcolor: safeAlpha(color, 0.15), color, fontWeight: 600 }} 
        />
      </Box>
      
      {(courseData[fileType] || []).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {(courseData[fileType] || []).map((file, index) => (
            <Chip
              key={index}
              label={file.name || (typeof file === 'string' ? file.split('\\').pop().split('/').pop() : '')}
              onDelete={() => handleRemoveFile(fileType, index)}
              onClick={() => {
                if (typeof file === 'string') {
                  window.open(`${process.env.REACT_APP_API_URL.replace('/api', '')}/${file}`, '_blank');
                } else if (file instanceof File) {
                  window.open(URL.createObjectURL(file), '_blank');
                }
              }}
              sx={{
                bgcolor: safeAlpha(color, 0.1),
                color: color,
                fontWeight: 500,
                cursor: 'pointer',
                '& .MuiChip-deleteIcon': { color }
              }}
            />
          ))}
        </Box>
      )}
      
      <input
        accept={fileType === 'coursePdfs' ? "application/pdf" : fileType === 'courseImages' ? "image/*" : "video/*"}
        style={{ display: 'none' }}
        id={`${fileType}-upload`}
        type="file"
        multiple
        onChange={(e) => handleFileChange(e, fileType)}
      />
      <label htmlFor={`${fileType}-upload`}>
        <Button 
          variant="outlined" 
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            borderColor: color,
            color: color,
            borderRadius: 2,
            fontWeight: 600,
            '&:hover': { borderColor: color, bgcolor: safeAlpha(color, 0.08) }
          }}
        >
          Subir {label}
        </Button>
      </label>
    </Box>
  );

  const renderStepContent = (step) => {
    if (!courseData) return null;

    switch (step) {
      case 0:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <InfoIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Información Básica
                </Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>
                  Edita el título, descripción y materiales del curso
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título del curso"
                  name="title"
                  value={courseData.title || ''}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: COLORS.primary },
                      '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Descripción del curso"
                  name="description"
                  value={courseData.description || ''}
                  onChange={handleInputChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: COLORS.primary },
                      '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoría del curso</InputLabel>
                  <Select
                    label="Categoría del curso"
                    name="category"
                    value={courseData.category || ''}
                    onChange={handleInputChange}
                    sx={{ borderRadius: 2 }}
                  >
                    {categories.map((category) => {
                      const catConfig = getCategoryConfig(category);
                      return (
                        <MenuItem key={category} value={category}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{catConfig.icon}</span>
                            <Typography>{category}</Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                {courseData.category && (
                  <Chip
                    label={`${getCategoryConfig(courseData.category).icon} ${courseData.category}`}
                    sx={{
                      bgcolor: safeAlpha(getCategoryConfig(courseData.category).color, 0.15),
                      color: getCategoryConfig(courseData.category).color,
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2.5,
                      px: 1
                    }}
                  />
                )}
              </Grid>
            </Grid>

            {/* Imagen de portada */}
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 3,
                border: '2px dashed #e0e0e0',
                bgcolor: safeAlpha(COLORS.purple, 0.02)
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark, mb: 2 }}>
                Imagen de Portada
              </Typography>
              
              {(courseData.coverImage || courseData.cover_image_path) && (
                <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
                  <img 
                    src={courseData.coverImage || `${process.env.REACT_APP_API_URL.replace('/api', '')}/${courseData.cover_image_path}`}
                    alt="Course cover" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '200px',
                      borderRadius: 8,
                      border: '2px solid #e0e0e0'
                    }} 
                  />
                  <IconButton 
                    onClick={() => setCourseData(prev => ({ 
                      ...prev, 
                      coverImage: null, 
                      cover_image_path: null 
                    }))}
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      bgcolor: COLORS.error,
                      color: 'white',
                      '&:hover': { bgcolor: safeAlpha(COLORS.error, 0.8) }
                    }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="cover-image-upload"
                  type="file"
                  onChange={handleCoverImageChange}
                />
                <label htmlFor="cover-image-upload">
                  <Button 
                    variant="outlined" 
                    component="span"
                    startIcon={<ImageIcon />}
                    sx={{
                      borderColor: COLORS.purple,
                      color: COLORS.purple,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': { borderColor: COLORS.purple, bgcolor: safeAlpha(COLORS.purple, 0.08) }
                    }}
                  >
                    {courseData.coverImage || courseData.cover_image_path ? 'Cambiar imagen' : 'Subir imagen'}
                  </Button>
                </label>
              </Box>
            </Paper>

            {/* Archivos adjuntos */}
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 3,
                borderRadius: 3,
                border: '2px solid #e0e0e0'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>
                Archivos Adjuntos
              </Typography>
              
              {renderFileList('coursePdfs', 'PDFs', <PictureAsPdfIcon />, COLORS.error)}
              {renderFileList('courseImages', 'Imágenes', <ImageIcon />, COLORS.success)}
              {renderFileList('courseVideos', 'Videos', <VideoLibraryIcon />, COLORS.primary)}
            </Paper>
          </Box>
        );

      case 1:
        return (
          <LessonCreator
            lessons={courseData.lessons || []}
            onChange={handleLessonsChange}
          />
        );

      case 2:
        return (
          <TestCreator
            test={courseData.test || { 
              questions: [], 
              config: {
                questions_per_test: 10,
                rotateQuestions: true,
                requireAllDifficulties: false
              }
            }}
            onChange={handleTestChange}
          />
        );

      case 3:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SettingsIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Configuración del Test
                </Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>
                  Define la puntuación mínima para aprobar
                </Typography>
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '2px solid #e0e0e0'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>
                Puntuación mínima para aprobar
              </Typography>
              <Typography variant="body2" sx={{ color: '#636e72', mb: 3 }}>
                Los usuarios deberán obtener al menos esta puntuación para recibir su certificado
              </Typography>
              
              <Box sx={{ px: 2 }}>
                <Slider
                  value={courseData.min_passing_score || 70}
                  onChange={handleMinPassingScoreChange}
                  valueLabelDisplay="on"
                  step={5}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 70, label: '70%' },
                    { value: 100, label: '100%' }
                  ]}
                  min={0}
                  max={100}
                  sx={{
                    color: COLORS.primary,
                    height: 8,
                    '& .MuiSlider-thumb': {
                      width: 24,
                      height: 24,
                      backgroundColor: 'white',
                      border: `3px solid ${COLORS.primary}`
                    },
                    '& .MuiSlider-track': {
                      background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                      border: 'none'
                    },
                    '& .MuiSlider-valueLabel': {
                      background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                      borderRadius: 2,
                      fontWeight: 700
                    }
                  }}
                />
              </Box>

              <Box 
                sx={{ 
                  mt: 4, 
                  p: 3, 
                  borderRadius: 2, 
                  bgcolor: (courseData.min_passing_score || 70) >= 70 
                    ? safeAlpha(COLORS.success, 0.1) 
                    : safeAlpha(COLORS.warning, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: (courseData.min_passing_score || 70) >= 70 ? COLORS.success : COLORS.warning,
                    fontWeight: 800,
                    fontSize: '1.2rem'
                  }}
                >
                  {courseData.min_passing_score || 70}%
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>
                    Puntuación mínima: {courseData.min_passing_score || 70}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#636e72' }}>
                    {(courseData.min_passing_score || 70) >= 70 
                      ? 'Nivel recomendado para formación profesional'
                      : 'Considera aumentar el umbral para mayor rigor'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Revisión y Guardar
                </Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>
                  Verifica los cambios y proporciona una razón (GMP)
                </Typography>
              </Box>
            </Box>

            {/* Resumen */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '2px solid #e0e0e0',
                mb: 3
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark, mb: 1 }}>
                    {courseData.title || 'Sin título'}
                  </Typography>
                  <Typography sx={{ color: '#636e72', mb: 2 }}>
                    {courseData.description || 'Sin descripción'}
                  </Typography>
                  {courseData.category && (
                    <Chip
                      label={`${getCategoryConfig(courseData.category).icon} ${courseData.category}`}
                      sx={{
                        bgcolor: safeAlpha(getCategoryConfig(courseData.category).color, 0.15),
                        color: getCategoryConfig(courseData.category).color,
                        fontWeight: 700
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  {(courseData.coverImage || courseData.cover_image_path) && (
                    <img 
                      src={courseData.coverImage || `${process.env.REACT_APP_API_URL.replace('/api', '')}/${courseData.cover_image_path?.replace(/\\/g, '/')}`}
                      alt="Course cover" 
                      style={{ 
                        width: '100%',
                        maxHeight: '150px',
                        objectFit: 'cover',
                        borderRadius: 8
                      }} 
                    />
                  )}
                </Grid>
              </Grid>

              {/* Stats */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                <Chip
                  icon={<MenuBookIcon />}
                  label={`${courseData.lessons?.length || 0} lecciones`}
                  sx={{ bgcolor: safeAlpha(COLORS.primary, 0.15), color: COLORS.primary, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.primary } }}
                />
                <Chip
                  icon={<QuizIcon />}
                  label={`${courseData.test?.questions?.length || 0} preguntas`}
                  sx={{ bgcolor: safeAlpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.purple } }}
                />
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Mín. ${courseData.min_passing_score || 70}%`}
                  sx={{ bgcolor: safeAlpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.success } }}
                />
              </Box>
            </Paper>

            {/* Lecciones Accordion */}
            <Accordion 
              elevation={0}
              sx={{ 
                borderRadius: '12px !important', 
                border: '2px solid #e0e0e0',
                mb: 2,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <MenuBookIcon sx={{ color: COLORS.primary }} />
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>
                    Lecciones ({courseData.lessons?.length || 0})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {courseData.lessons?.map((lesson, index) => (
                  <Accordion key={index} elevation={0} sx={{ bgcolor: '#f8fffe', mb: 1, borderRadius: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: safeAlpha(COLORS.primary, 0.15), color: COLORS.primary, fontSize: '0.8rem', fontWeight: 700 }}>
                          {index + 1}
                        </Avatar>
                        <Typography sx={{ fontWeight: 600 }}>{lesson.title}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box 
                        sx={{ '& img': { maxWidth: '100%' } }}
                        dangerouslySetInnerHTML={{ __html: lesson.content }} 
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>

            {/* Test Accordion */}
            <Accordion 
              elevation={0}
              sx={{ 
                borderRadius: '12px !important', 
                border: '2px solid #e0e0e0',
                mb: 3,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <QuizIcon sx={{ color: COLORS.purple }} />
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>
                    Test Final ({courseData.test?.questions?.length || 0} preguntas)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Distribución */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>
                    Distribución por dificultad
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label={`Básico: ${courseData.test?.questions?.filter(q => q.difficulty === 'basico').length || 0}`}
                      sx={{ bgcolor: safeAlpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600 }}
                    />
                    <Chip 
                      label={`Intermedio: ${courseData.test?.questions?.filter(q => q.difficulty === 'intermedio').length || 0}`}
                      sx={{ bgcolor: safeAlpha(COLORS.warning, 0.15), color: COLORS.warning, fontWeight: 600 }}
                    />
                    <Chip 
                      label={`Avanzado: ${courseData.test?.questions?.filter(q => q.difficulty === 'avanzado').length || 0}`}
                      sx={{ bgcolor: safeAlpha(COLORS.error, 0.15), color: COLORS.error, fontWeight: 600 }}
                    />
                  </Box>
                </Box>

                {courseData.test?.questions?.map((question, index) => (
                  <Accordion key={index} elevation={0} sx={{ bgcolor: '#f8fffe', mb: 1, borderRadius: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mr: 2 }}>
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>
                          {index + 1}. {question.question}
                        </Typography>
                        <Chip 
                          label={question.difficulty || 'básico'}
                          size="small"
                          sx={{
                            bgcolor: safeAlpha(
                              question.difficulty === 'avanzado' ? COLORS.error :
                              question.difficulty === 'intermedio' ? COLORS.warning : COLORS.success, 0.15
                            ),
                            color: question.difficulty === 'avanzado' ? COLORS.error :
                                   question.difficulty === 'intermedio' ? COLORS.warning : COLORS.success,
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {question.options?.map((option, optIndex) => (
                          <ListItem key={optIndex}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {option === question.correctAnswer ? 
                                <CheckCircleOutlineIcon sx={{ color: COLORS.success }} /> : 
                                <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd' }} />
                              }
                            </ListItemIcon>
                            <ListItemText 
                              primary={option}
                              primaryTypographyProps={{
                                sx: { 
                                  fontWeight: option === question.correctAnswer ? 600 : 400,
                                  color: option === question.correctAnswer ? COLORS.success : COLORS.dark
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>

            {/* Razón del cambio (GMP) */}
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              <Typography sx={{ fontWeight: 600 }}>Requerido para cumplimiento GMP</Typography>
              <Typography variant="body2">
                Debe proporcionar una razón para documentar los cambios realizados.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Razón del cambio (GMP)"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              required
              placeholder="Ej: Actualización de contenido por cambio normativo..."
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: COLORS.warning },
                  '&.Mui-focused fieldset': { borderColor: COLORS.warning }
                }
              }}
            />

            {/* Botones de acción */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={handleOpenHistory}
                sx={{
                  borderColor: COLORS.info,
                  color: COLORS.info,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { borderColor: COLORS.info, bgcolor: safeAlpha(COLORS.info, 0.08) }
                }}
              >
                Ver Historial
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<DeleteIcon />}
                onClick={handleDeleteCourse}
                sx={{
                  borderColor: COLORS.error,
                  color: COLORS.error,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { borderColor: COLORS.error, bgcolor: safeAlpha(COLORS.error, 0.08) }
                }}
              >
                Eliminar curso
              </Button>
            </Box>
          </Box>
        );

      default:
        return 'Paso desconocido';
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)'
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: `0 8px 32px ${safeAlpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <EditIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: 600, mb: 2 }}>
          Cargando curso...
        </Typography>
        <Box sx={{ width: 200 }}>
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 6,
              backgroundColor: safeAlpha(COLORS.gradient1, 0.2),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                borderRadius: 2
              }
            }} 
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/courses')}
          sx={{ 
            mt: 3,
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
          }}
        >
          Volver a cursos
        </Button>
      </Container>
    );
  }

  if (!courseData) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">No se encontró el curso.</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fffe', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.warning} 0%, ${COLORS.orange} 100%)`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 20px 60px ${safeAlpha(COLORS.warning, 0.3)}`
          }}
        >
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EditIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 900, letterSpacing: -0.5 }}>
                Editar Curso
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                {courseData.title}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Stepper */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '2px solid #e0e0e0'
          }}
        >
          <Stepper activeStep={activeStep} connector={<CustomConnector />}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: index <= activeStep 
                          ? `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
                          : '#e0e0e0',
                        color: index <= activeStep ? 'white' : '#9e9e9e',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {index < activeStep ? <CheckCircleIcon /> : step.icon}
                    </Box>
                  )}
                >
                  <Typography 
                    sx={{ 
                      fontWeight: index === activeStep ? 700 : 500,
                      color: index === activeStep ? COLORS.primary : '#636e72'
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Content */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            border: '2px solid #e0e0e0',
            mb: 3
          }}
        >
          {renderStepContent(activeStep)}
        </Paper>

        {/* Navigation */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: '2px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{
              borderColor: COLORS.primary,
              color: COLORS.primary,
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                borderColor: COLORS.primary,
                bgcolor: safeAlpha(COLORS.primary, 0.08)
              },
              '&.Mui-disabled': {
                borderColor: '#e0e0e0',
                color: '#bdbdbd'
              }
            }}
          >
            Atrás
          </Button>

          {/* Indicador central */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: index === activeStep ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: index <= activeStep 
                    ? `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
                    : '#e0e0e0',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Box>

          {activeStep === steps.length - 1 ? (
            <Button 
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={isSubmitting || !changeReason.trim()}
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                py: 1.5,
                boxShadow: `0 4px 14px ${safeAlpha(COLORS.gradient1, 0.4)}`,
                '&:hover': {
                  boxShadow: `0 6px 20px ${safeAlpha(COLORS.gradient1, 0.5)}`
                },
                '&.Mui-disabled': {
                  background: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          ) : (
            <Button 
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                boxShadow: `0 4px 14px ${safeAlpha(COLORS.gradient1, 0.4)}`,
                '&:hover': {
                  boxShadow: `0 6px 20px ${safeAlpha(COLORS.gradient1, 0.5)}`
                }
              }}
            >
              Siguiente
            </Button>
          )}
        </Paper>

        {/* History Dialog */}
        <Dialog 
          open={historyOpen} 
          onClose={() => setHistoryOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <HistoryIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Historial de cambios</Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {history.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
                <Typography sx={{ color: '#636e72' }}>No hay historial de cambios</Typography>
              </Box>
            ) : (
              <List>
                {history.map((change, index) => (
                  <ListItem key={index} sx={{ borderBottom: '1px solid #e0e0e0', py: 2 }}>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: COLORS.info }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {new Date(change.change_date).toLocaleString()}
                          </Typography>
                          <Chip
                            label={change.change_type_display || change.change_type}
                            size="small"
                            sx={{
                              bgcolor: safeAlpha(
                                change.change_type === 'UPDATE' ? COLORS.primary :
                                change.change_type === 'DELETE' ? COLORS.error : COLORS.success, 0.15
                              ),
                              color: change.change_type === 'UPDATE' ? COLORS.primary :
                                     change.change_type === 'DELETE' ? COLORS.error : COLORS.success,
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" sx={{ color: '#636e72' }}>
                            <strong>Modificado por:</strong> {change.modified_by_name} {change.modified_by_lastname}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#636e72' }}>
                            <strong>Razón:</strong> {change.reason}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button 
              onClick={() => setHistoryOpen(false)}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default EditCourse;